import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

interface InfractionType {
  id: string;
  name: string;
  description: string;
  category: string;
  pointsDeducted: number;
  severity: string;
  sortOrder: number;
  isActive: boolean;
}

const CATEGORIES = [
  { value: 'qc', label: 'Quality Control' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'safety', label: 'Safety' },
  { value: 'policy', label: 'Policy' },
  { value: 'conduct', label: 'Conduct' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES = [
  { value: 'minor', label: 'Minor', color: 'bg-blue-100 text-blue-800' },
  { value: 'moderate', label: 'Moderate', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'major', label: 'Major', color: 'bg-orange-100 text-orange-800' },
  { value: 'severe', label: 'Severe', color: 'bg-red-100 text-red-800' },
];

function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full">
          <div className="border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6 max-h-[80vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const config = SEVERITIES.find(s => s.value === severity) || SEVERITIES[0];
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

export default function InfractionTypesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [types, setTypes] = useState<InfractionType[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<InfractionType | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    pointsDeducted: '5',
    severity: 'moderate',
    sortOrder: '0',
    isActive: true,
  });

  useEffect(() => {
    loadTypes();
  }, [showInactive]);

  const loadTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest(`/discipline/admin/infraction-types${showInactive ? '?includeInactive=true' : ''}`);
      setTypes(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load infraction types');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      category: 'other',
      pointsDeducted: '5',
      severity: 'moderate',
      sortOrder: '0',
      isActive: true,
    });
    setShowModal(true);
  };

  const openEditModal = (type: InfractionType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      category: type.category || 'other',
      pointsDeducted: type.pointsDeducted?.toString() || '5',
      severity: type.severity || 'moderate',
      sortOrder: type.sortOrder?.toString() || '0',
      isActive: type.isActive,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        category: formData.category,
        pointsDeducted: parseInt(formData.pointsDeducted) || 5,
        severity: formData.severity,
        sortOrder: parseInt(formData.sortOrder) || 0,
        isActive: formData.isActive,
      };

      if (editingType) {
        await apiRequest(`/discipline/admin/infraction-types/${editingType.id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
        setSuccess('Infraction type updated');
      } else {
        await apiRequest('/discipline/admin/infraction-types', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        setSuccess('Infraction type created');
      }

      closeModal();
      loadTypes();
    } catch (err: any) {
      setError(err.message || 'Failed to save infraction type');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (type: InfractionType) => {
    try {
      await apiRequest(`/discipline/admin/infraction-types/${type.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !type.isActive }),
      });
      loadTypes();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  // Group types by category
  const groupedTypes = types.reduce((acc, type) => {
    const cat = type.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(type);
    return acc;
  }, {} as Record<string, InfractionType[]>);

  return (
    <>
      <PageHeader
        title="Infraction Types"
        subtitle="Manage discipline infraction categories and settings"
        actions={
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show inactive
            </label>
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Type
            </button>
          </div>
        }
      />

      {error && (
        <div className="mb-4 bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 text-green-700 px-4 py-3 rounded-lg">{success}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <div className="space-y-6">
          {CATEGORIES.map(cat => {
            const catTypes = groupedTypes[cat.value] || [];
            if (catTypes.length === 0) return null;

            return (
              <Card key={cat.value}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{cat.label}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-500 text-sm border-b">
                        <th className="pb-3 font-medium">Name</th>
                        <th className="pb-3 font-medium">Description</th>
                        <th className="pb-3 font-medium">Severity</th>
                        <th className="pb-3 font-medium text-center">Points</th>
                        <th className="pb-3 font-medium text-center">Order</th>
                        <th className="pb-3 font-medium text-center">Status</th>
                        <th className="pb-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catTypes.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map(type => (
                        <tr key={type.id} className={`border-b hover:bg-gray-50 ${!type.isActive ? 'opacity-50' : ''}`}>
                          <td className="py-3 font-medium">{type.name}</td>
                          <td className="py-3 text-gray-600 text-sm max-w-xs truncate">{type.description}</td>
                          <td className="py-3"><SeverityBadge severity={type.severity} /></td>
                          <td className="py-3 text-center text-red-600 font-medium">-{type.pointsDeducted}</td>
                          <td className="py-3 text-center text-gray-500">{type.sortOrder || 0}</td>
                          <td className="py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${type.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {type.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => openEditModal(type)}
                                className="p-1 text-gray-500 hover:text-blue-600"
                                title="Edit"
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleToggleActive(type)}
                                className={`p-1 ${type.isActive ? 'text-gray-500 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`}
                                title={type.isActive ? 'Deactivate' : 'Activate'}
                              >
                                {type.isActive ? (
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}

          {types.length === 0 && (
            <Card>
              <div className="text-center py-8 text-gray-500">
                No infraction types defined. Click "Add Type" to create one.
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title={editingType ? 'Edit Infraction Type' : 'Add Infraction Type'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Tardiness"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Description of the infraction..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={e => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SEVERITIES.map(sev => (
                  <option key={sev.value} value={sev.value}>{sev.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Points Deducted</label>
              <input
                type="number"
                value={formData.pointsDeducted}
                onChange={e => setFormData({ ...formData, pointsDeducted: e.target.value })}
                onWheel={e => e.currentTarget.blur()}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={e => setFormData({ ...formData, sortOrder: e.target.value })}
                onWheel={e => e.currentTarget.blur()}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingType ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
